import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  isChunkError: boolean;
}

/**
 * Chytá chyby z renderu i z lazy-load chunků (route code-splitting). Bez ní by selhání
 * fetche chunku — typicky po deployi za běhu session nebo při výpadku sítě — skončilo
 * bílou obrazovkou. Nabídne "načíst znovu", což u chunk chyb spolehlivě pomůže.
 *
 * Class komponenta, protože error boundaries nemají hook ekvivalent. i18n bereme přes
 * `i18n.t` (boundary se renderuje až po initu, takže překlady jsou k dispozici).
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: unknown): State {
    const msg = error instanceof Error ? `${error.name} ${error.message}` : String(error);
    // Vite/Rollup hlásí selhání dynamického importu různě dle prohlížeče.
    const isChunkError = /ChunkLoadError|dynamically imported module|Importing a module script failed|Failed to fetch/i.test(msg);
    return { hasError: true, isChunkError };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-neon-cyan flex items-center justify-center p-8">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-xl font-display text-neon-magenta">{i18n.t('common.errorBoundaryTitle')}</h1>
          <p className="text-sm opacity-70">{i18n.t('common.errorBoundaryHint')}</p>
          <button
            onClick={this.handleReload}
            className="px-6 py-2 bg-gradient-to-r from-neon-cyan to-neon-magenta text-slate-950 rounded-full font-display tracking-widest"
          >
            {i18n.t('common.reload')}
          </button>
        </div>
      </div>
    );
  }
}
